import { WalletPayment } from '@aaa-ncnu-ie/mypolicy-api-billing';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { parse, format, isValid as isValidDate } from 'date-fns';
import React from 'react';
import { Link } from 'react-router-dom';

import PencilEditIcon from '../../assets/icons/PencilEdit';
import colors from '../../constants/colors';
import paymentTypeMap from '../../constants/paymentTypeNames';
import { getWalletAccount, getWalletCard } from '../../utils/wallet';

interface PaymentMethodOptionButtonProps {
  account: WalletPayment;
}

const formatExpirationDate = (expirationDate?: string): string => {
  if (!expirationDate) return '';
  const parsed = parse(expirationDate, 'MM/dd/yyyy', new Date());
  return parsed && isValidDate(parsed) ? format(parsed, 'MM/yy') : '';
};

const PaymentMethodOptionButton = ({
  account,
}: PaymentMethodOptionButtonProps) => {
  const card = getWalletCard(account);
  const bankAccount = getWalletAccount(account);

  const getButtonText = () => {
    if (account.shortName) {
      return account.shortName;
    }

    if (card) {
      return `${paymentTypeMap[card.type]} ****${card.number}`;
    } else if (bankAccount) {
      return `${paymentTypeMap[bankAccount.type]} ****${bankAccount.number}`;
    }
  };

  const getAriaLabelText = () => {
    if (card) {
      const expirationDate = formatExpirationDate(card?.expirationDate);
      return `Edit payment method for ${
        account?.shortName ? account?.shortName : paymentTypeMap[card?.type]
      } ****${card?.number} expiration date ${expirationDate}`;
    } else {
      return `Edit payment method for ${
        account?.shortName
          ? account?.shortName
          : paymentTypeMap?.[bankAccount?.type ?? '']
      } ****${bankAccount?.number}`;
    }
  };

  return (
    <ButtonBase
      aria-label={getAriaLabelText()}
      component={Link}
      relative="path"
      to="edit"
      state={{
        walletAccount: account,
      }}
      sx={{
        width: '100%',
        p: 2,
        borderRadius: 2,
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: colors.csaaNeutral,
        '&:focus': {
          borderRadius: 1,
          bgcolor: colors.csaaOverlayBlack,
          outline: `solid 2px ${colors.csaaBlack}`,
        },
      }}
      key={`${account.paymentAccountToken}`}
    >
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          width: '100%'
        }}>
        <Stack
          sx={{
            alignItems: "flex-start",
            gap: 0.5
          }}>
          <Stack
            direction={{ mobile: 'column', tablet: 'row' }}
            sx={{
              alignItems: { mobile: 'flex-start', tablet: 'center' },
              gap: { mobile: 1, tablet: 2 }
            }}>
            <Typography
              className="csaa-qm-block"
              sx={{
                fontSize: { mobile: 19, tablet: 23 },
                lineHeight: { mobile: '24px', tablet: '32px' },
                letterSpacing: { mobile: 0.4, tablet: 0.6 },
                fontWeight: 'bold',
                color: colors.csaaBlack,
              }}
            >
              {getButtonText()}
            </Typography>
            {card?.expired && (
              <Box
                sx={{
                  backgroundColor: colors.csaaRed10,
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                  height: 24,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Cabin',
                    fontKerning: 'normal',
                    fontSize: 14,
                    lineHeight: '16px',
                    letterSpacing: 0.2,
                    color: colors.csaaDark,
                    fontWeight: 500,
                  }}
                >
                  Expired
                </Typography>
              </Box>
            )}
          </Stack>
          {card && (
            <Typography
              sx={{
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                fontSize: { mobile: 16, tablet: 18 },
                lineHeight: '24px',
                letterSpacing: { mobile: 0.3, tablet: 0.4 },
                color: colors.csaaMid,
              }}
            >
              Exp {formatExpirationDate(card?.expirationDate)}
            </Typography>
          )}
        </Stack>
        <Box sx={{ p: 1 }}>
          <PencilEditIcon />
        </Box>
      </Stack>
    </ButtonBase>
  );
};

export default PaymentMethodOptionButton;
